/**
 * The one `AudioContext` every sound this CRM makes for itself comes out of.
 *
 * Every other sound module used to build its own, and for the test beep or a
 * microphone graph that is right - both are started by a click on the control
 * that makes them, so the browser has a gesture to point at and the context
 * comes up running. The incoming ring is the one sound in this module with NO
 * gesture behind it by definition: a call arrives while nobody is touching the
 * machine. A context built at that moment is born `suspended`, produces no
 * sound at all, and reports no error while doing it - which is how an operator
 * ends up with a phone that has been silent since the tab was opened and no way
 * to find out.
 *
 * So the ring does not build its own. It uses this one, which was built and
 * unlocked by the first click or keypress anywhere in the CRM - hours earlier,
 * on a gesture that had nothing to do with the phone.
 *
 * ⚠ THE SHARED CONTEXT IS NEVER CLOSED, and that is the rule that makes it
 * work. `AudioContext.close()` is one-way: a closed context cannot be reopened,
 * so the first hang-up would leave the rest of the session silent. Callers
 * dispose of their own nodes and their own sink and leave the context alone.
 * This is a deliberate reversal of the rule `gain.ts` follows for the
 * MICROPHONE context, which is closed precisely because an open one holds an
 * input device and keeps the tab's recording indicator lit. An output-only
 * context holds neither.
 */

type Ctor = typeof AudioContext;

/**
 * `AudioContext`, or Safari's prefixed one, or nothing at all.
 *
 * The single copy. It was written out inline in four other files, and four
 * copies of a browser-support check is how the fifth one ends up subtly
 * different from the rest.
 */
export function audioContextCtor(): Ctor | null {
  if (typeof window === "undefined") return null;
  return window.AudioContext ?? (window as { webkitAudioContext?: Ctor }).webkitAudioContext ?? null;
}

let shared: AudioContext | null = null;

/** Build once, then hand the same one out forever. Null where there is none. */
function build(): AudioContext | null {
  if (shared) return shared;
  const Ctor = audioContextCtor();
  if (!Ctor) return null;
  try {
    shared = new Ctor();
  } catch {
    // A browser that has the constructor and refuses to run it. Nothing to be
    // done, and the incoming card still carries the call.
    return null;
  }
  return shared;
}

/**
 * What state the shared context is in, WITHOUT building one.
 *
 * `"none"` covers both "nothing has needed audio yet" and "this browser has no
 * AudioContext". The pickers ask this to decide what to warn about, and asking
 * must not itself be the thing that creates a context.
 */
export function audioContextState(): AudioContextState | "none" {
  return shared ? shared.state : "none";
}

/**
 * Take the first gesture anywhere in the CRM and spend it on the ring.
 *
 * Two events, not one: Chrome and Safari both accept a keypress as activation
 * just as readily as a click, and an operator who drives the CRM from the
 * keyboard would otherwise never unlock the phone.
 *
 * CAPTURE PHASE, on `window`. The CRM is full of popovers and a call window
 * scrim that stop `pointerdown` from propagating; a bubbling listener would
 * miss exactly the clicks an operator makes most.
 *
 * The listeners are NOT `{ once: true }` and are not removed after the first
 * success. A context that is running at nine in the morning can be suspended
 * again hours later by the OS or by tab throttling, and the next gesture is the
 * only thing that can bring it back - so the handler stays and costs an early
 * return per click for the rest of the session.
 */
export function armAudioUnlock(): () => void {
  if (typeof window === "undefined") return () => {};

  const unlock = () => {
    const ctx = build();
    if (!ctx || ctx.state === "running") return;
    void ctx.resume().catch(() => {});
  };

  window.addEventListener("pointerdown", unlock, true);
  window.addEventListener("keydown", unlock, true);
  return () => {
    window.removeEventListener("pointerdown", unlock, true);
    window.removeEventListener("keydown", unlock, true);
  };
}

/**
 * The shared context, built and awake, ready to be scheduled against.
 *
 * ALWAYS AWAIT THIS BEFORE SCHEDULING. A suspended context has a frozen
 * `currentTime`, so oscillators started against it are queued at a moment that
 * never arrives - the sound is not late, it never happens, and the nodes pile
 * up silently. Resolving null means there is no audio to be had here.
 *
 * The `resume()` is attempted even without a gesture, because it costs one
 * rejected promise and occasionally works: a page that has been interacted with
 * at any point keeps its activation, so a context suspended by the OS while the
 * operator was in another app usually comes straight back.
 */
export async function readyAudioContext(): Promise<AudioContext | null> {
  const ctx = build();
  if (!ctx) return null;
  if (ctx.state === "suspended") {
    await ctx.resume().catch(() => {});
  }
  return ctx.state === "running" ? ctx : null;
}

/**
 * Why the phone would be silent if it rang right now.
 *
 * Four answers, and the order they are asked in is the point: an operator who
 * has turned the ring off themselves does not need telling that their browser
 * has not been clicked in. Only `"ready"` means a call would be heard.
 */
export type RingGate = "ready" | "muted" | "reduced-motion" | "locked";

export interface RingGateInput {
  /** `audioContextState()` - "none" before anything has needed audio. */
  state: AudioContextState | "none";
  /** `readAudioVolume().ring`. Zero is the operator's own answer. */
  volume: number;
  /** `prefers-reduced-motion: reduce`, which `useRingtone` treats as silence. */
  reducedMotion: boolean;
}

/**
 * Pure, so the one thing anybody actually wants to know - will the phone make a
 * sound - can be read and tested without a browser to make sounds in.
 *
 * `"none"` counts as ready rather than locked. Nothing has asked for audio yet,
 * so nothing has been refused yet, and the first ring builds the context inside
 * the gesture that armed the unlock. Reporting a problem that has not happened
 * would put a warning on every freshly loaded tab.
 */
export function ringGate(input: RingGateInput): RingGate {
  if (input.volume <= 0) return "muted";
  if (input.reducedMotion) return "reduced-motion";
  if (input.state === "suspended" || input.state === "closed") return "locked";
  return "ready";
}
